import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import WOTemplateForm from '../components/WOTemplateForm';
import useWOTemplateCreate from '../hooks/useWOTemplateCreate';
import useWOTemplateQuery from '../hooks/useWOTemplateQuery';
import type { WorkOrderTemplate } from '#/graphql/types';
import { createFields } from '../components/WOTemplateForm/fields';
import useWorkOrderQuery from '#/modules/workOrder/hooks/useWorkOrderQuery';
import htmlToText from '#/common/utils/parsers/htmlToText';

const NewWOTemplatesPage: React.FC = () => {
  const title = 'New WO Template';
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copyFrom') || undefined;
  const createFromWorkOrderId = searchParams.get('createFromWorkOrder') || undefined;

  const { data: copyFromData, loading: copyFromLoading } = useWOTemplateQuery(copyFromId);
  const sourceTemplate = copyFromData?.getWorkOrderTemplate?.data;

  const { data: sourceWorkOrderData, loading: sourceWorkOrderLoading } = useWorkOrderQuery(
    createFromWorkOrderId,
    { skip: !createFromWorkOrderId },
  );
  const sourceWorkOrder = sourceWorkOrderData?.getWorkOrder?.data;

  const newWOTemplate = useMemo(() => {
    const number = crypto.getRandomValues(new Uint32Array(1))[0] % 10000;

    if (createFromWorkOrderId) {
      if (sourceWorkOrder) {
        return {
          number,
          title: sourceWorkOrder.title ?? '',
          description: htmlToText(sourceWorkOrder.description),
          estimatedEffort: sourceWorkOrder.actualEffort ?? undefined,
          type: sourceWorkOrder.type ?? undefined,
          category: sourceWorkOrder.category ?? undefined,
          impact: sourceWorkOrder.impact ?? undefined,
          priority: sourceWorkOrder.priority ?? undefined,
          notes: sourceWorkOrder.notes ?? undefined,
          tenantId: sourceWorkOrder.tenantId ?? undefined,
          assignedToId: sourceWorkOrder.assignedToId ?? undefined,
          assetTypeId: sourceWorkOrder.asset?.assetTypeId ?? undefined,
          assetManufacturerId:
            sourceWorkOrder.asset?.makeId ?? sourceWorkOrder.asset?.model?.manufacturerId ?? undefined,
          assetModelId: sourceWorkOrder.asset?.modelId ?? undefined,
        } as WorkOrderTemplate;
      }

      return {
        number,
      } as WorkOrderTemplate;
    }

    if (copyFromId && sourceTemplate) {
      const {
        id,
        displayNumber,
        createdAt,
        createdBy,
        createdByUser,
        updatedAt,
        updatedBy,
        updatedByUser,
        userId, // Not used but remove it just in case
        locationId, // Not used but remove it just in case
        assignmentGroupId, // Not used but remove it just in case
        manufacturer,
        assetManufacturerId,
        assetType,
        assetTypeId,
        model,
        assetModelId,
        tenant,
        ...rest
      } = sourceTemplate;

      return {
        ...rest,
        title: `${rest.title} - Copy`,
        number,
      };
    }
    return {
      number,
    } as WorkOrderTemplate;
  }, [copyFromId, createFromWorkOrderId, sourceTemplate, sourceWorkOrder]);

  const { createWOTemplate } = useWOTemplateCreate();

  return (
    <SubPage
      title={title}
      showGoBackButton
      type="form"
      isLoading={copyFromLoading || sourceWorkOrderLoading}>
      <WOTemplateForm
        title={title}
        woTemplate={newWOTemplate}
        fields={createFields}
        submit={createWOTemplate}
      />
    </SubPage>
  );
};

export default React.memo(NewWOTemplatesPage);
